import _ from 'lodash';
import { DateTime } from 'luxon';
import { TIMETABLE_TIMEZONE } from '../constants';
import { SessionsCache } from './SessionsCache';
import ApiHelper from './ApiHelper';
import {
  dateToStartOfDayUTC,
  dateToEndOfDayUTC,
  datetimeToDateInTimezone,
  getDaysBetween,
} from '../../shared/utils/datetime';

/**
 * @typedef {Omit<
 *   import('./ApiHelper').ApiHelperSessionsReqArgs,
 *   'apiKey' | 'startDateGte' | 'startDateLte'
 * >} CacheGroupKeyArgs
 */

/**
 * Get Sessions, either from the cache or from the Search API, to render via the
 * Timetable.
 */
export const GetSessions = {
  /**
   * For a particular day (and other optional filters), get the sessions from the
   * cache. If they are not in the cache, get them from imin Search and populate
   * the cache with the results.
   *
   * @param {object} config
   * @param {string} config.timetableWindowStartDate ISO 8601 date string.
   *   The first date that the timetable shows (usually today).
   * @param {string} config.timetableWindowEndDate ISO 8601 date string.
   *   The last date that the timetable shows (e.g. 7 days from today).
   * @param {string} apiKey
   * @param {string} date ISO 8601 date string
   * @param {CacheGroupKeyArgs} args
   */
  async fromCacheOrSearchApi(config, apiKey, date, args) {
    const normalizedArgs = normalizeArgs(args);
    const cacheGroupKey = JSON.stringify(normalizedArgs);

    const cachedSessions = SessionsCache.get(cacheGroupKey, date);
    if (cachedSessions) {
      return cachedSessions;
    }

    await GetSessions.requestAndStoreToCache(config, apiKey, date, args, cacheGroupKey);
    return SessionsCache.get(cacheGroupKey, date);
  },

  /**
   * Request the sessions from the Search API and store them in the cache.
   *
   * @param {object} config
   * @param {string} config.timetableWindowStartDate ISO 8601 date string.
   *   The first date that the timetable shows (usually today).
   * @param {string} config.timetableWindowEndDate ISO 8601 date string.
   *   The last date that the timetable shows (e.g. 7 days from today).
   * @param {string} apiKey
   * @param {string} dateStr ISO 8601 date string
   * @param {CacheGroupKeyArgs} cacheGroupArgs
   * @param {string} cacheGroupKey
   */
  async requestAndStoreToCache(config, apiKey, dateStr, cacheGroupArgs, cacheGroupKey) {
    const startDateGte = dateToStartOfDayUTC(dateStr, TIMETABLE_TIMEZONE);
    /* Get the end date for the request.
    We choose either
    1). The succeeding cached date if there is one (e.g. if this is day 2, and
    the cached dates are day 1 and day 5, then we have no need to look at beyond
    day 4).
    2). The end of the timetable window if there is no succeeding cached date
    */
    const cachedDates = SessionsCache.getDates(cacheGroupKey);
    cachedDates.sort();
    const nextCachedDate = getNextItemInSortedArray(cachedDates, dateStr);

    const endDateStr = (() => {
      if (nextCachedDate) {
        // There's a cached date after this one, request until the day before it
        return DateTime.fromISO(nextCachedDate).minus({ days: 1 }).toISODate();
      } else {
        // No cached date after this one, request until end of timetable window
        return config.timetableWindowEndDate;
      }
    })();
    const startDateLte = dateToEndOfDayUTC(endDateStr, TIMETABLE_TIMEZONE);

    // Make the API request and populate the cache
    const response = await ApiHelper.sessions({
      apiKey,
      startDateGte,
      startDateLte,
      ...cacheGroupArgs,
    });

    // Early return if no valid response
    if (!response || !response['imin:item']) {
      return;
    }

    // Process response and populate cache for multiple days based on task logic
    const sessions = response['imin:item'];
    const totalItems = response['imin:totalItems'];
    const responseItemLimit = ApiHelper.defaultLimit;

    // Group sessions by date (converting to TIMETABLE_TIMEZONE first)
    const sessionsByDate = {};
    for (const session of sessions) {
      // Convert session startDate to TIMETABLE_TIMEZONE then extract date
      const sessionDate = datetimeToDateInTimezone(
        session.subEvent[0].subEvent[0].startDate,
        TIMETABLE_TIMEZONE
      );
      if (!sessionsByDate[sessionDate]) {
        sessionsByDate[sessionDate] = [];
      }
      sessionsByDate[sessionDate].push(session);
    }

    // Get all days in the range we're processing
    const allDaysInRange = getDaysBetween(dateStr, endDateStr, TIMETABLE_TIMEZONE);
    const isCompleteData = totalItems <= responseItemLimit;

    // Iterate through each day in the range using index
    for (let i = 0; i < allDaysInRange.length; i++) {
      const currentDate = allDaysInRange[i];

      // Always cache the requested day (caveat: this means that we cannot get
      // any more than 50 sessions in one day. Note that this caveat has been in
      // play since the beginning of Timetable)
      if (currentDate === dateStr) {
        SessionsCache.set(cacheGroupKey, currentDate, sessionsByDate[currentDate] || []);
        continue;
      }

      // For complete data (totalItems <= 50), cache everything
      if (isCompleteData) {
        SessionsCache.set(cacheGroupKey, currentDate, sessionsByDate[currentDate] || []);
        continue;
      }

      // For incomplete data, apply the existing rules
      if (sessionsByDate[currentDate]) {
        // This day has sessions - check if this day's data is complete, by
        // checking if any subsequent days have sessions
        const hasSubsequentSessions = (() => {
          for (let j = i + 1; j < allDaysInRange.length; j++) {
            if (sessionsByDate[allDaysInRange[j]]) {
              return true;
            }
          }
          return false;
        })();

        if (!hasSubsequentSessions) {
          // No more sessions after this day - we've reached the end of reliable data
          break;
        }

        // Cache this day since we have subsequent days with sessions
        SessionsCache.set(cacheGroupKey, currentDate, sessionsByDate[currentDate]);
      } else {
        // This day has no sessions - always cache it as empty
        // (We only reach here if we're still in the reliable range)
        SessionsCache.set(cacheGroupKey, currentDate, []);
      }
    }
  },
};

/**
 * @param {string[]} array
 * @param {string} item
 * @returns {string | undefined}
 */
function getNextItemInSortedArray(array, item) {
  return array.find((dateStr) => dateStr > item);
}

/**
 * Normalize args so that they can form a consistent cache key.
 *
 * 1. Remove any null/undefined key/values
 * 2. Return the object as array entries, sorted by key
 *
 * @param {CacheGroupKeyArgs} args
 * @returns {[
 *   key: keyof CacheGroupKeyArgs,
 *   value: CacheGroupKeyArgs[keyof CacheGroupKeyArgs]
 * ][]}
 */
function normalizeArgs(args) {
  const entries = Object.entries(args)
    .filter(([, value]) => !_.isNil(value))
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

  return /** @type {any} */ (entries);
}
